<template>
  <div>
    <v-autocomplete
       ref="autocomplete"
      :disabled="disabled || field.isReadOnly"
      v-bind:label="labelText"
      v-model="field.value"
      :items="listElements"
      item-text="text"
      item-value="text"
      :loading="isLoading"
      :search-input.sync="search"
      :hint="field.observations"
      :error-messages="fieldErrors"
      :clearable="!field.isReadOnly"
      :multiple="field.multiValue"
      :deletable-chips="field.multiValue"
      :cache-items="field.hasPerformanceQuery && field.multiValue"
      @click:clear.stop.prevent.self="onClear"
      @change="onChange"
      @input="validator.document.fields.$each[index].value.$touch();"
    >
      <template slot="append-outer">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              color="success"
              icon
              v-show="showAddData"
              @click="addInteralTableData"
            >
              <v-icon>mdi-table-plus</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("InsertData") }}</span>
        </v-tooltip>
      </template>
      <template v-if="field.multiValue" v-slot:selection="{ item, index }">
        <v-chip
          :key="index"
          close
          small
          class="ma-1"
          @click:close="removeItem(item)"
        >
          {{ item.text }}
        </v-chip>
      </template>
    </v-autocomplete>
    <AddDataModal :ref="modalKey" @on-item-saved="onInternalDataSaved" />
  </div>
</template>
<script>
import ContentTypeService from '@/services/ContentTypeService';
import FieldType from '@/ecm/variables/FieldType';
import { EventBus } from '@/eventBus.js';
import AddDataModal from '@/components/document/documentModal/internalTable/AddDataModal';
import Utils from '@/ecm/common/Utils';

export default {
    components: {
        AddDataModal,
    },
    props: {
        field: {
            type: Object,
            required: true,
        },
        validator: {
            type: Object,
            default: () => ({}),
        },
        index: {
            type: Number,
        },
        ctIndex: {
            type: Number,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        canAddData: {
            type: Boolean,
            default: false,
        },
        fieldChangeSource: {
            type: Number,
            default: 0
        },
        documentFields: {
            type: Array,
            default: () => []
        },
        isLastVersionOfContentType: {
            type: Boolean,
            default: false
        }
    },
    mounted() {
        this.getListElements();
        EventBus.$on('refreshListElements' + this.field.code, (parameters, isBeingCleared) => {
            this.getListElements(parameters, isBeingCleared);
        });
    },
    destroyed() {
        EventBus.$off('refreshListElements' + this.field.code);
    },
    data() {
        return {
            search: null,
            listElements: [],
            filteredListElements: [],
            isLoading: false,
            modalKey: 'modalAddData',
            isFirstTime: true,
            isBeingCleared: false
        };
    },
    methods: {
        onInternalDataSaved() {
            this.getListElements();
            this.modalAddData.hide();
        },
        addInteralTableData() {
            if (this.showAddData) {
                this.modalAddData.showModal(
                    this.field.defFieldId,
                    this.field.internalTableId
                );
            }
        },
        getListElements(parameters, isBeingCleared) {
            if (isBeingCleared) {
                this.$refs.autocomplete.cachedItems = [];
                this.field.value = null;
            }

            if (this.field.type == FieldType.StaticList) {
                this.listElements = this.field.listElements || [];
                if (
                    this.listElements.length > 0 &&
                    this.validator.document &&
                    this.validator.document.$model.id == 0 &&
                    !this.field.value &&
                    !this.field.hasRecognitionConfig
                ) {
                    this.field.value = this.listElements[0].text;
                }
            } else if (
                this.field.type == FieldType.DynamicList ||
                this.field.type == FieldType.DynamicTableList
            ) {
                if (this.field.fieldsOnWhichDepends.length > 0 && !parameters) {
                    return;
                }
                this.isLoading = true;
                const filterQuery = {
                    FirstTime: this.isFirstTime,
                    Parameters: parameters,
                };
                this.getListElementsByDefField(this.field.defFieldId, filterQuery, isBeingCleared);
                this.isFirstTime = false;
            }
        },
        getListElementsByDefField(defFieldID, filterQuery, avoidSelectingFirstItem = false) {
            ContentTypeService.getListElementsByDefField(defFieldID, filterQuery)
                .then((result) => {
                    if (result.length > 5000 && this.isLastVersionOfContentType) {
                        Utils.toast.warning(this.$t('Site.DocumentFieldList.HighNumberOfRecordsDetected').replace('{0}', this.field.title));
                    }

                    this.listElements = result;
                    this.listElements = this.listElements.filter((e) => e.text != '');
                    const listElementsText = this.listElements.map(e => e.text);
                    if (this.field.value && this.field.hasPerformanceQuery) {
                        if (this.field.multiValue) {
                            for (var fieldValue of this.field.value) {
                                if (!listElementsText.includes(fieldValue)) {
                                    const value = {
                                        id: fieldValue,
                                        selected: false,
                                        text: fieldValue
                                    };
                                    this.$refs.autocomplete.cachedItems.push(value);
                                }
                            }
                        } else if (!listElementsText.includes(this.field.value)) {
                            const value = {
                                id: this.field.value,
                                selected: false,
                                text: this.field.value
                            };
                            this.$refs.autocomplete.cachedItems.push(value);
                        }
                    }
                    if (result.length === 1 && !avoidSelectingFirstItem)
                        this.field.value = this.field.multiValue ? [result[0].text] : result[0].text;
                })
                .catch((error) => {
                    this.disabled = true;
                    throw error;
                })
                .finally((x) => (this.isLoading = false));
        },
        fetchEntriesDebounced(search) {
            if (this.field.type == FieldType.StaticList) return;

            var filterQuery = { Filter: search };
            if (this.field.fieldsOnWhichDepends.length > 0) {
                const parameters = [];
                this.field.fieldsOnWhichDepends.forEach((fieldCode) => {
                    const fieldDependsOn = this.documentFields.filter(
                        (f) => f.code == fieldCode
                    )[0];

                    let paramValue = fieldDependsOn.value;
                    let multiValue = false;
                    if (Array.isArray(paramValue)) {
                        paramValue = paramValue.filter((x) => x).join(',');
                        multiValue = true;
                    }
                    parameters.push({
                        Name: fieldCode,
                        Value: paramValue || '',
                        Multivalue: multiValue,
                    });
                });
                filterQuery.Parameters = parameters;
            }

            clearTimeout(this._timerId);
            this._timerId = setTimeout(() => {
                this.getListElementsByDefField(this.field.defFieldId, filterQuery, true);
            }, 500);
        },
        onClear() {
            this.field.isBeingCleared = true;
        },
        onChange() {
            EventBus.$emit('field-edited', this.field, this.fieldChangeSource);
        },
        removeItem(item) {
            this.field.value = this.field.value.filter(v => v !== item.text);
            this.field.isBeingCleared = true;
        },
    },
    computed: {
        fieldErrors() {
            const errors = [];
            if (this.validator.document) {
                if (!this.validator.document.fields.$each[this.index].value.$dirty)
                    return errors;

                !this.validator.document.fields.$each[this.index].value.required &&
                    errors.push(this.$t('RequiredField'));
            } else if (this.validator.contentTypeList) {
                if (
                    !this.validator.contentTypeList.$each[this.ctIndex].document.fields
                        .$each[this.index].value.$dirty
                )
                    return errors;

                !this.validator.contentTypeList.$each[this.ctIndex].document.fields
                    .$each[this.index].value.required &&
                    errors.push(this.$t('RequiredField'));
            }

            return errors;
        },
        showAddData() {
            return (
                this.field.type == FieldType.DynamicTableList &&
                this.canAddData &&
                this.field.internalTableId > 0
            );
        },
        modalAddData() {
            return this.$refs[this.modalKey];
        },
        labelText() {
            return `${this.field.title}${this.field.isRequired ? ' *' : ''}`;
        }
    },
    watch: {
        search(val) {
            val !== this.field.value && this.fetchEntriesDebounced(val);
        }
    },
};
</script>
<style>
.v-input__append-outer {
  margin-top: 0px;
  margin-bottom: 0px;
}
</style>
